/**
 * 📢 MARKETING API SERVICE - Integration with backend marketing messages APIs
 */

import { getApiBaseUrl } from '@/config/environment';


// Types for Marketing Messages - Based on actual API response
export interface MarketingMessage {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  type: 'card' | 'banner' | 'modal';
  icon?: string;
  callToAction: {
    text: string;
    route?: string;
    externalUrl?: string;
  };
  styling: {
    backgroundColor: string;
    borderColor: string;
    textColor: string;
    template: string;
  };
  targeting: {
    userRoles: string[];
    locations: string[];
  };
  schedule: {
    startDate: string;
    endDate?: string;
  };
  analytics: {
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: string;
  };
  metadata: {
    priority: number;
    variant: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface CreateMessageRequest {
  title: string;
  subtitle?: string;
  description: string; // This is what backend calls "content"
  type?: 'card' | 'banner' | 'modal';
  status?: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  icon?: string;
  callToAction?: {
    text: string;
    route?: string;
    externalUrl?: string;
  };
  targeting?: {
    userRoles: string[];
    locations?: string[];
  };
  schedule?: {
    startDate?: string;
    endDate?: string;
  };
  metadata?: {
    priority?: number;
    variant?: string;
  };
}

export interface MessageAnalytics {
  totalMessages: number;
  activeMessages: number;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  averageCTR: number;
  averageConversionRate: number;
  messagePerformance: {
    messageId: string;
    title: string;
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
    conversionRate: number;
  }[];
}

export interface GetMessagesResponse {
  success: boolean;
  data: {
    messages: MarketingMessage[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface GetAnalyticsResponse {
  success: boolean;
  data: MessageAnalytics;
}

export const marketingApiService = {
  
  /**
   * 📋 Get all marketing messages (admin)
   */
  async getMessages(filters?: {
    status?: string;
    messageType?: string;
    targetRole?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<GetMessagesResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.messageType) queryParams.append('messageType', filters.messageType);
      if (filters?.targetRole) queryParams.append('targetRole', filters.targetRole);
      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.search) queryParams.append('search', filters.search);

      const url = `${getApiBaseUrl()}/api/v1/marketing/messages${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // Handle the actual API response structure
      if (result.success && result.messages) {
        return {
          success: true,
          data: {
            messages: result.messages,
            pagination: result.pagination
          }
        };
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching marketing messages:', error);
      
      return {
        success: false,
        data: {
          messages: []
        }
      };
    }
  },

  /**
   * ✏️ Create new marketing message
   */
  async createMessage(messageData: CreateMessageRequest): Promise<{
    success: boolean;
    data?: MarketingMessage;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/marketing/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error creating marketing message:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create message'
      };
    }
  },

  /**
   * 📝 Update marketing message
   */
  async updateMessage(messageId: string, messageData: Partial<CreateMessageRequest>): Promise<{
    success: boolean;
    data?: MarketingMessage;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/marketing/messages?id=${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error updating marketing message:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update message'
      };
    }
  },

  /**
   * 🗑️ Delete marketing message
   */
  async deleteMessage(messageId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/marketing/messages?id=${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error deleting marketing message:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete message'
      };
    }
  },

  /**
   * 📊 Get marketing analytics
   */
  async getAnalytics(): Promise<GetAnalyticsResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/marketing/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching marketing analytics:', error);
      
      return {
        success: false,
        data: {
          totalMessages: 0,
          activeMessages: 0,
          totalImpressions: 0,
          totalClicks: 0,
          totalConversions: 0,
          averageCTR: 0,
          averageConversionRate: 0,
          messagePerformance: []
        }
      };
    }
  },

  /**
   * 📈 Track message interaction
   */
  async trackInteraction(messageId: string, eventType: 'impression' | 'click' | 'conversion', userId: string, location?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/marketing/messages/${messageId}/track`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventType,
          userId,
          location
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error tracking marketing interaction:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to track interaction'
      };
    }
  }
};